import type { Anchor, OverlayLayout, VirtualAnchor } from './overlay.types'

/**
 * Layout dispatch engine — pure decisions, no DOM access and no side effects,
 * so both are testable with synthetic inputs: `resolveLayout` picks the layout
 * token from the anchor, `sheetRenderFor` picks which of `sheet`'s two renders
 * a box of a given width gets.
 *
 * Thresholds are policy constants, not magic numbers — changes are visible
 * as named-constant diffs, not hidden in inline literals.
 */

/** Floating UI / CSS anchor-positioning safety padding. */
export const ANCHOR_FIT_PADDING_PX = 16

/** Tailwind's `md` breakpoint, and the complement of `isMobile$`'s own
 *  `(max-width: 767px)` query: at or above this width `sheet` renders as a
 *  centered card, below it as a bottom-anchored sheet. */
export const SHEET_CENTERED_MIN_WIDTH_PX = 768

/**
 * Which of `sheet`'s two renders is showing.
 *
 * `media` is the window answering in CSS, through the `md:` prefixes on the
 * surface's own class string — every unbounded overlay, and the one member
 * nothing here produces.
 *
 * `bottom`/`centered` are resolved from a measured width, because a bounded
 * overlay lays out inside its boundary's box rather than the
 * window's, and a media query cannot see that box: a phone frame drawn 375px
 * wide on a 1280px screen would otherwise render the desktop card inside the
 * drawn phone.
 */
export type SheetRender = 'media' | 'bottom' | 'centered'

export function sheetRenderFor(boxWidthPx: number): Exclude<SheetRender, 'media'> {
	return boxWidthPx >= SHEET_CENTERED_MIN_WIDTH_PX ? 'centered' : 'bottom'
}

/** On a narrow box, the share of its area an anchored panel may cover and still
 *  read as a panel beside its trigger. A third of a phone screen is a detail
 *  panel, not a menu or a one-line note: past it nothing useful is left beside
 *  the panel, so it opens as the sheet. */
export const CROWDING_AREA_SHARE = 1 / 3

export interface Size {
	width: number
	height: number
}

/**
 * Whether a panel of this measured size, laid out in this box, is the screen's
 * subject rather than a panel beside its trigger. Only a box narrow enough for
 * the sheet's bottom render can be crowded: at or above
 * `SHEET_CENTERED_MIN_WIDTH_PX` every anchored panel stays where the gesture put
 * it, whatever its size.
 */
export function crowdsNarrowBox(panel: Size, box: Size): boolean {
	return sheetRenderFor(box.width) === 'bottom' && panel.width * panel.height >= box.width * box.height * CROWDING_AREA_SHARE
}

export interface LayoutInputs {
	/** The anchor as `resolveAnchorRef` resolved it — the same value the
	 *  positioner places the surface against, so an overlay can never be laid
	 *  out anchored against an anchor nothing can place it beside. */
	anchor?: ResolvedAnchor
	/** The content, measured as the anchored panel it would open as, crowds a
	 *  narrow box (`crowdsNarrowBox`). Only ever measured when a gesture anchored
	 *  the open and the box is narrow. */
	crowded?: boolean
	/** The overlay lays out inside a boundary box rather than the window: the box
	 *  every size decision measures against, and — for the sheet — which of its
	 *  two renders shows (`sheetRenderFor`). It names no layout. */
	bounded?: boolean
	/** The one layout an in-package caller names for itself, carried on the
	 *  internal `ResolvedShowOptions` and never on the public `ShowOptions`.
	 *  True only for content that IS the thing being looked at. */
	viewer?: boolean
}

/**
 * Three outcomes. The gesture and the content's measured size decide two of
 * them; `viewer` is asked for by name, because no gesture can reveal what the
 * content IS.
 *
 * - viewer asked for         → viewer   (a photo, a page: the content owns the viewport)
 * - anchor, content fits     → anchored (popover/menu/dropdown next to a trigger)
 * - anchor, content crowds   → sheet    (a large panel on a phone: nothing fits beside it)
 * - no anchor                → sheet    (everything else: dialogs, content panels)
 *
 * A panel crowds only a narrow box (`crowdsNarrowBox`): on a phone a large
 * detail panel has no trigger-adjacent room left to sit in, so it docks to the
 * bottom edge the way a messenger's detail sheet does. A wide box never
 * converts.
 *
 * `viewer` is read BEFORE the anchor: content that owns the viewport is still
 * opened from a tap on the thing it blooms out of, so it carries an anchor, and
 * that anchor is the origin the entrance pivots about rather than a request for
 * the small panel beside a trigger. The anchor is not discarded — it answers two
 * questions at once, where the surface belongs and where its entrance grows
 * from, and `viewer` takes the first while leaving the second: `overlay.component`'s
 * `setAnchorOriginVars` reads the anchor on the modal tier exactly as it does on
 * an anchored one.
 *
 * A boundary is not a fourth answer. It says which box the surface lays out in
 * and which box crowding is measured against, never a layout of its own — so a
 * bounded overlay is exactly what the same view does on the phone a device frame
 * draws: a small panel stays anchored inside the frame, a crowding one docks to
 * the frame's bottom edge.
 *
 * Downstream, `overlay.component`'s `open()` clears `bounded` for a non-modal
 * tier and hands the frame to Floating UI as its clipping box: the anchored
 * panel is placed and clamped inside the frame it cannot otherwise see.
 *
 * No public option names a layout. A consumer says what to show, and this
 * function says where it belongs; which of the sheet's two renders the reader
 * meets — the bottom sheet at a phone's width, the centered card above it — is
 * resolved downstream from a measured box. `ShowOptions` carries no `layout`
 * field for a consumer to pass; the internal `ResolvedShowOptions.layout`
 * accepts the single value `'viewer'` and is reachable only from inside this
 * package, the same containment `anchor` already has and for the same reason.
 *
 * Floating UI's `flip` + `shift` + `size` middleware handles "anchor doesn't
 * fit initially" — it returns a fitting position rather than rejecting. So
 * anchored layouts don't need a fallback; Floating UI's output IS the answer.
 */
export function resolveLayout(inputs: LayoutInputs): OverlayLayout {
	if (inputs.viewer) return 'viewer'
	return inputs.anchor !== undefined && !inputs.crowded ? 'anchored' : 'sheet'
}

/**
 * Resolved reference the positioning strategies consume.
 *
 * `el` is populated when the anchor is an HTMLElement — the Popover API
 * tier uses it directly via the `anchor=<id>` attribute, and Tier 1 / CSS
 * Anchor Positioning sets `element.style.anchorName` on it.
 *
 * `virtual` is populated for point/rect/TouchEvent anchors — Floating UI
 * accepts any object exposing `getBoundingClientRect()` as a virtual
 * reference. The Popover API path cannot use virtual references, so a
 * caller passing a point / DOMRect / event forces Tier 3 (or Tier 2 with
 * Floating UI, not CSS Anchor Positioning).
 *
 * `kind` discriminates positioning strategy:
 *   element = real HTMLElement trigger (flip, trigger-side-respecting, byte-identical to today)
 *   rect    = sized virtual e.g. boat pill (flip, trigger-side-respecting, byte-identical to today)
 *   point   = zero-size click coord (autoPlacement, most-space grow into roomiest vertical region)
 */
export interface ResolvedAnchor {
	el?: HTMLElement
	virtual?: VirtualAnchor
	kind: 'element' | 'point' | 'rect'
}

/**
 * Normalise an `Anchor` input into the shape the positioning strategies
 * accept. One function, one decision: element wins if present; otherwise
 * synthesize a zero-size virtual reference at the anchor's point.
 */
export function resolveAnchorRef(anchor: Anchor | undefined): ResolvedAnchor | undefined {
	if (!anchor) return undefined

	if (typeof HTMLElement !== 'undefined' && anchor instanceof HTMLElement) {
		return { el: anchor, kind: 'element' }
	}

	// Any other object with getBoundingClientRect — sized virtual (e.g. boat pill).
	if (typeof (anchor as { getBoundingClientRect?: unknown }).getBoundingClientRect === 'function') {
		return {
			virtual: {
				getBoundingClientRect: () => (anchor as { getBoundingClientRect(): DOMRect }).getBoundingClientRect(),
			},
			kind: 'rect',
		}
	}

	// DOMRect — fixed rect; synthesize a virtual that returns it verbatim.
	if (
		typeof (anchor as DOMRect).width === 'number' &&
		typeof (anchor as DOMRect).height === 'number' &&
		typeof (anchor as DOMRect).left === 'number' &&
		typeof (anchor as DOMRect).top === 'number'
	) {
		const rect = anchor as DOMRect
		return {
			virtual: {
				getBoundingClientRect: () => rect,
			},
			kind: 'rect',
		}
	}

	// MouseEvent / PointerEvent — zero-size rect at click point, tracked live
	// off the element the click landed on (see `makeLiveElementAnchor`).
	if (typeof (anchor as MouseEvent).clientX === 'number' && typeof (anchor as MouseEvent).clientY === 'number') {
		const e = anchor as MouseEvent
		const target = resolveEventTarget(e)
		if (target) return { virtual: makeLiveElementAnchor(target, e.clientX, e.clientY), kind: 'point' }
		const rect = makePointRect(e.clientX, e.clientY)
		return { virtual: { getBoundingClientRect: () => rect }, kind: 'point' }
	}

	// TouchEvent — zero-size rect at first touch point, same live tracking.
	if (typeof (anchor as TouchEvent).touches !== 'undefined' && (anchor as TouchEvent).touches.length > 0) {
		const te = anchor as TouchEvent
		const t = te.touches[0]
		const target = resolveEventTarget(te)
		if (target) return { virtual: makeLiveElementAnchor(target, t.clientX, t.clientY), kind: 'point' }
		const rect = makePointRect(t.clientX, t.clientY)
		return { virtual: { getBoundingClientRect: () => rect }, kind: 'point' }
	}

	// { x, y } point — zero-size rect at coords. Guard the coords: this is the
	// last-resort branch, and `kind: 'point'` routes to autoPlacement — a
	// malformed anchor (non-numeric x/y) would otherwise become a NaN rect that
	// autoPlacement mis-positions silently. An unresolvable anchor returns
	// undefined, which degrades the overlay to the modal tier (centred) rather
	// than a NaN-positioned surface.
	const pt = anchor as { x: number; y: number }
	if (!Number.isFinite(pt.x) || !Number.isFinite(pt.y)) return undefined
	const rect = makePointRect(pt.x, pt.y)
	return { virtual: { getBoundingClientRect: () => rect }, kind: 'point' }
}

/**
 * The control a resolved anchor hangs off, for the two questions that are
 * about the trigger rather than the geometry: whether a pointerdown landed on
 * the control that owns this panel, and whether a fresh gesture is
 * re-activating a control whose panel is already open.
 *
 * Deliberately not `el`. `el` is the positioner's reference — `positionFloatingUI`
 * reads `anchor.el ?? anchor.virtual` — so populating it for a point anchor would
 * silently move every gesture-opened overlay in the repo off the click point and
 * onto the clicked element's whole box: a repo-wide change of placement nobody
 * asked for, from a field that exists to answer a different question.
 * The point anchor already carries its clicked element as the virtual
 * reference's `contextElement` (Floating UI's own field, set by
 * `makeLiveElementAnchor` so `autoUpdate` can climb that element's ancestors);
 * reading it back here answers "which control is this" without widening what
 * `el` means to the positioner.
 *
 * Undefined for the anchors that genuinely have no owning control — a bare
 * `{ x, y }`, a DOMRect, and the boat's own rect anchor, none of which a user
 * can click a second time.
 */
export function anchorTrigger(anchor: ResolvedAnchor | undefined): Element | undefined {
	return anchor?.el ?? anchor?.virtual?.contextElement
}

/**
 * `Event.target` after `Event.currentTarget` — `target` is retained by the
 * platform after dispatch finishes (what `resolveAnchorRef` always sees,
 * since it runs later, off the ambient-anchor tracker's remembered event);
 * `currentTarget` is nulled by then, kept here only as a second chance for a
 * caller that reaches this synchronously mid-dispatch.
 */
function resolveEventTarget(e: Event): Element | null {
	if (e.target instanceof Element) return e.target
	if (e.currentTarget instanceof Element) return e.currentTarget
	return null
}

/**
 * Live-tracking virtual anchor for a click/tap origin. `getBoundingClientRect()`
 * returns a zero-size point at the SAME offset from `target` it was captured
 * at — so the very first read (no reflow yet) reproduces today's click-point
 * placement exactly — but recomputed from `target`'s LIVE rect on every call,
 * so a later reposition (driven by `contextElement` below wiring `autoUpdate`'s
 * ancestor-scroll listener) reads where the clicked element visually is now,
 * not the frozen screen pixel the pointer was at when this closure was built.
 * Degrades to the frozen point whenever `target` has no layout box, which a
 * still-connected element can lose: `schmancy-menu` keeps its panel rows in
 * light DOM and stops slotting them when the menu closes, so the row that was
 * clicked to open an overlay survives that gesture connected but unrendered,
 * and reading it live yields 0×0 at the viewport origin. An element marked
 * `data-overlay-anchor-still` moves on its own, as a flying bird does, rather
 * than with what it sits in, so what opened from it stands where it opened.
 */
function makeLiveElementAnchor(target: Element, clientX: number, clientY: number): VirtualAnchor {
	const initial = target.getBoundingClientRect()
	const offsetX = clientX - initial.left
	const offsetY = clientY - initial.top
	const frozen = makePointRect(clientX, clientY)
	return {
		contextElement: target,
		getBoundingClientRect: () => {
			if (target.getClientRects().length === 0 || target.closest('[data-overlay-anchor-still]')) return frozen
			const live = target.getBoundingClientRect()
			return makePointRect(live.left + offsetX, live.top + offsetY)
		},
	}
}

/**
 * Construct a DOMRect-compatible object for a zero-width / zero-height
 * point. Avoids `new DOMRect(...)` which isn't constructable across all
 * lib.dom.d.ts vintages.
 */
function makePointRect(x: number, y: number): DOMRect {
	return {
		x,
		y,
		width: 0,
		height: 0,
		left: x,
		right: x,
		top: y,
		bottom: y,
		toJSON() {
			return { x, y, width: 0, height: 0, left: x, right: x, top: y, bottom: y }
		},
	}
}
